import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "@/components/icons";
import { SavingButton } from "@/components/settings/SavingButton";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";

export function PasswordChangeForm() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (next !== confirm) {
      setError("New passwords do not match");
      return;
    }
    if (next.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setSaving(true);
    // Verify current password by attempting sign-in (preserves existing pattern).
    const { data: sess } = await supabase.auth.getSession();
    const email = sess.session?.user.email;
    if (email) {
      const { error: vErr } = await supabase.auth.signInWithPassword({ email, password: current });
      if (vErr) {
        setSaving(false);
        setError("Current password is incorrect");
        return;
      }
    }
    const { error: uErr } = await supabase.auth.updateUser({ password: next });
    setSaving(false);
    if (uErr) {
      setError(uErr.message);
      return;
    }
    setCurrent("");
    setNext("");
    setConfirm("");
    toast.success("Password updated");
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <Field label="Current password" autoComplete="current-password" value={current} onChange={setCurrent} />
      <Field label="New password" autoComplete="new-password" value={next} onChange={setNext} minLength={8} />
      <Field label="Confirm new password" autoComplete="new-password" value={confirm} onChange={setConfirm} />
      <div className="flex justify-end pt-2">
        <SavingButton saving={saving} type="submit" disabled={!current || !next || !confirm}>
          Update password
        </SavingButton>
      </div>
    </form>
  );
}

function Field({
  label,
  autoComplete,
  value,
  onChange,
  minLength,
}: {
  label: string;
  autoComplete: string;
  value: string;
  onChange: (v: string) => void;
  minLength?: number;
}) {
  const id = `pw-${label.toLowerCase().replace(/\s+/g, "-")}`;
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="password"
        autoComplete={autoComplete}
        required
        minLength={minLength}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
